import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from 'src/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-sm hover:brightness-110 hover:-translate-y-px hover:shadow-md',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 hover:-translate-y-px',
        outline:
          'border border-input bg-background text-accent shadow-sm hover:bg-muted hover:border-primary',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:brightness-90 hover:-translate-y-px hover:shadow-md',
        ghost: 'hover:bg-accent/10 hover:text-accent',
        link: 'text-primary underline-offset-4 hover:underline',
        amber:
          'bg-secondary text-secondary-foreground shadow-sm hover:brightness-90 hover:-translate-y-px hover:shadow-md',
        text: 'bg-transparent text-primary px-1 hover:text-accent hover:underline',
      },
      size: {
        default: 'h-9 px-5 py-2',
        sm: 'h-8 rounded-md px-3.5 py-1.5 text-xs',
        lg: 'h-10 px-7 text-[15px]',
        xl: 'h-12 px-9 text-base rounded-xl',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), loading && 'pointer-events-none')}
        ref={ref}
        disabled={asChild ? undefined : disabled || loading}
        {...props}>
        {asChild ? (
          children
        ) : (
          <>
            {loading && (
              <span className='h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent' />
            )}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
